import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

const requestFullscreen = (el) => {
    if (el.requestFullscreen) return el.requestFullscreen()
    if (el.webkitRequestFullscreen) return el.webkitRequestFullscreen()
    if (el.msRequestFullscreen) return el.msRequestFullscreen()
    return Promise.reject(new Error('Fullscreen API not supported'))
}

const exitFullscreen = () => {
    if (document.exitFullscreen) return document.exitFullscreen()
    if (document.webkitExitFullscreen) return document.webkitExitFullscreen()
    if (document.msExitFullscreen) return document.msExitFullscreen()
    return Promise.reject(new Error('Fullscreen API not supported'))
}

const getFullscreenElement = () => document.fullscreenElement || document.webkitFullscreenElement || document.msFullscreenElement || null

const FS_CHANGE_EVENTS = ['fullscreenchange', 'webkitfullscreenchange', 'msfullscreenchange']

export function useFullScreenHandle() {
    const node = useRef(null)
    const [active, setActive] = useState(false)

    useEffect(() => {
        const onChange = () => setActive(getFullscreenElement() === node.current)
        FS_CHANGE_EVENTS.forEach(ev => document.addEventListener(ev, onChange))
        return () => FS_CHANGE_EVENTS.forEach(ev => document.removeEventListener(ev, onChange))
    }, [])

    // if a sibling element already holds fullscreen, exit first then enter on ours (mirrors react-full-screen behaviour)
    const enter = useCallback(() => {
        if (!node.current) return Promise.resolve()
        if (getFullscreenElement()) return exitFullscreen().then(() => requestFullscreen(node.current))
        return requestFullscreen(node.current)
    }, [])

    const exit = useCallback(() => {
        if (getFullscreenElement() !== node.current) return Promise.resolve()
        return exitFullscreen()
    }, [])

    return useMemo(() => ({ active, enter, exit, node }), [active, enter, exit])
}

// onChange fires whenever the handle's active flips — matches react-full-screen's contract (single arg: the new active value)
export function FullScreen({ handle, onChange, children, className }) {
    useEffect(() => {
        onChange?.(handle.active, handle)
    }, [handle.active]) // eslint-disable-line react-hooks/exhaustive-deps

    const classes = ['fullscreen']
    if (className) classes.unshift(className)
    if (handle.active) classes.push('fullscreen-enabled')

    return (
        <div
            ref={handle.node}
            className={classes.join(' ')}
            style={handle.active ? { height: '100%', width: '100%' } : undefined}
        >
            {children}
        </div>
    )
}
